import {
    CaretSortIcon,
    CheckIcon
} from '@radix-ui/react-icons';
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import '../../App.css';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../ui/command";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover";
import { DataTable } from './datatable';
import DatatableSortButton from './datatableSortButton';
import { formatDateToYYYYMMDD } from '../../lib/dateHelper';

export const columns = () => [
    {
        accessorKey: "name",
        header: ({ column }) => <DatatableSortButton column={column}>Namn</DatatableSortButton>,
        cell: info => {
            const competition = info.row.original;
            return <Link to={`/competition/${competition.id}`} className='text-blue-600 font-medium hover:underline'>{competition.name}</Link>;
        },
    },
    {
        accessorKey: "clubName",
        header: ({ column }) => <DatatableSortButton column={column}>Förening</DatatableSortButton>,
        cell: info => {
            const competition = info.row.original;
            return competition.clubName;
        }
    },
    {
        accessorKey: "end_date",
        header: ({ column }) => <DatatableSortButton column={column}>Slutdatum</DatatableSortButton>,
        cell: info => {
            const competition = info.row.original;
            return formatDateToYYYYMMDD(competition.end_date);
        }
    },
];

export function CompetitionResultsDatatable() {
    const [sorting, setSorting] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [columnFilters, setColumnFilters] = useState([]);
    const [competitions, setCompetitions] = useState([]);
    const [clubs, setClubs] = useState([]);
    const [selectedClub, setSelectedClub] = useState(null);
    const form = useForm();
    const [filteredCompetitions, setFilteredCompetitions] = useState([]);
    const [comboboxOpen, setComboboxOpen] = useState(false);

    const fetchCompetitions = async () => {
        setIsLoading(true);
        try {
            const response = await api.get('https://skhrf-api.cmdigital.se/v1/competitions/list-result');

            const transformedCompetitions = response.data.map(item => ({
                ...item.competitions,
                clubName: item.clubName,
                clubId: item.competitions.club_id, // Extract club ID for filtering
            }));
            setCompetitions(transformedCompetitions);

            // Extract clubs from competition data
            const extractedClubs = Array.from(
                new Map(
                    response.data.map(item => [item.competitions.club_id, { id: item.competitions.club_id, name: item.clubName }])
                ).values()
            );
            setClubs(extractedClubs);
        } catch (error) {
            console.error('Error fetching competitions:', error);
            setCompetitions([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCompetitions();
    }, []);

    useEffect(() => {
        const today = new Date();
        const pastCompetitions = competitions.filter(competition => {
            const endDate = new Date(competition.end_date);
            return endDate < today;
        });

        pastCompetitions.sort((a, b) => {
            const endDateA = new Date(a.end_date);
            const endDateB = new Date(b.end_date);
            return endDateB - endDateA;
        });

        if (selectedClub) {
            const filteredByClub = pastCompetitions.filter(
                competition => competition.club_id === selectedClub
            );
            setFilteredCompetitions(filteredByClub);
        } else {
            setFilteredCompetitions(pastCompetitions);
        }
    }, [competitions, selectedClub]);

    // Initialize table
    const table = useReactTable({
        data: filteredCompetitions,
        columns: columns(),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnFilters,
        },
    });

    return (
        <>
            <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 py-3'>
                <div className='flex gap-3'>
                    <Form {...form} >
                        <form className="flex flex-col lg:flex-row w-full">
                            <FormField
                                control={form.control}
                                name="clubName"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel>Förening</FormLabel>
                                        <Popover modal={true} open={comboboxOpen} onOpenChange={setComboboxOpen}>
                                            <PopoverTrigger asChild>
                                                <FormControl>
                                                    <Button
                                                        variant="outline"
                                                        role="combobox"
                                                        disabled={isLoading}
                                                        className={cn(
                                                            "min-w-[300px] justify-between",
                                                            !field.value && "text-muted-foreground"
                                                        )}
                                                    >
                                                        {field.value
                                                            ? clubs.find(
                                                                (club) => club.id === field.value
                                                            )?.name
                                                            : "Välj förening"}
                                                        <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                    </Button>
                                                </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="popover-content-width-full p-0">
                                                <Command>
                                                    <CommandInput placeholder="Sök förening..." />
                                                    <CommandList>
                                                        <CommandEmpty>Ingen förening hittades.</CommandEmpty>
                                                        <CommandGroup>
                                                            {clubs.map((club) => (
                                                                <CommandItem
                                                                    className='cursor-pointer'
                                                                    value={club.id}
                                                                    key={club.id}
                                                                    onSelect={() => {
                                                                        if (selectedClub === club.id) {
                                                                            form.setValue("clubName", null);
                                                                            setSelectedClub(null);
                                                                        } else {
                                                                            form.setValue("clubName", club.id);
                                                                            setSelectedClub(club.id);
                                                                        }
                                                                        setComboboxOpen(false)
                                                                    }}
                                                                >
                                                                    <CheckIcon
                                                                        className={cn(
                                                                            "mr-2 h-4 w-4",
                                                                            club.id === form.watch("clubName") ? "opacity-100" : "opacity-0"
                                                                        )}
                                                                    />
                                                                    {club.name}
                                                                </CommandItem>
                                                            ))}

                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </form>
                    </Form>
                </div>
            </div>
            <DataTable
                table={table}
                isLoading={isLoading}
                columns={columns()}
                skeletonRows={10}
            />
        </>
    );
}
